/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';

      this.progressMarks = [10, 25, 50, 75];
      this.completeThreshold = 98;
      this.youtubeProgressTimer = null;
      this.observer = null;
      this.initialized = false;

      this.resetTrackingState();
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.init();
    }

    disconnectedCallback() {
      this.stopYouTubeProgressPolling();

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    resetTrackingState() {
      this.hasStarted = false;
      this.hasCompleted = false;
      this.firedProgressMarks = new Set();
    }

    normalizeNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    }

    shouldTrackVideo() {
      return !this.background && this.dataset.trackVideo !== 'false';
    }

    canSendAnalytics() {
      if (typeof window.gtag !== 'function') return false;

      if (
        window.Shopify?.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
      ) {
        return window.Shopify.customerPrivacy.analyticsProcessingAllowed();
      }

      return true;
    }

    getVideoTitle() {
      return this.dataset.videoTitle || this.dataset.description || this.videoId || 'video';
    }

    getVideoUrl() {
      const videoEl = this.querySelector('video');
      return this.dataset.videoUrl || videoEl?.currentSrc || videoEl?.src || '';
    }

    markPlayed() {
      const section = this.closest('.video-section');
      if (section) section.classList.add('video-section--played');
    }

    buildGA4VideoPayload(currentTime = 0, duration = 0) {
      const safeCurrentTime = this.normalizeNumber(currentTime);
      const safeDuration = this.normalizeNumber(duration);
      const percent = safeDuration > 0
        ? Math.min(100, Math.floor((safeCurrentTime / safeDuration) * 100))
        : 0;

      return {
        video_current_time: Math.round(safeCurrentTime),
        video_duration: Math.round(safeDuration),
        video_percent: percent,
        video_provider: this.type === 'tag' ? 'html5' : this.type,
        video_title: this.getVideoTitle(),
        video_url: this.getVideoUrl(),
        ...(this.dataset.productId ? { product_id: this.dataset.productId } : {}),
        ...(this.dataset.productTitle ? { product_title: this.dataset.productTitle } : {})
      };
    }

    sendGA4Event(eventName, payload) {
      if (!this.canSendAnalytics()) return;
      window.gtag('event', eventName, payload);
    }

    trackVideoStart(currentTime = 0, duration = 0) {
      if (!this.shouldTrackVideo() || this.hasStarted) return;

      this.hasStarted = true;
      this.sendGA4Event('video_start', this.buildGA4VideoPayload(currentTime, duration));
    }

    trackVideoProgress(currentTime = 0, duration = 0) {
      if (!this.shouldTrackVideo() || this.hasCompleted) return;

      const payload = this.buildGA4VideoPayload(currentTime, duration);
      const nextMark = this.progressMarks.find(
        (mark) => payload.video_percent >= mark && !this.firedProgressMarks.has(mark)
      );

      if (nextMark) {
        this.firedProgressMarks.add(nextMark);
        this.sendGA4Event('video_progress', {
          ...payload,
          video_percent: nextMark
        });
      }

      this.checkVideoComplete(currentTime, duration);
    }

    checkVideoComplete(currentTime = 0, duration = 0) {
      if (!this.shouldTrackVideo() || this.hasCompleted) return;

      const safeCurrentTime = this.normalizeNumber(currentTime);
      const safeDuration = this.normalizeNumber(duration);
      if (!safeDuration) return;

      const percent = Math.floor((safeCurrentTime / safeDuration) * 100);

      if (percent >= this.completeThreshold) {
        this.hasCompleted = true;
        this.sendGA4Event('video_complete', {
          ...this.buildGA4VideoPayload(safeDuration, safeDuration),
          video_percent: 100
        });
      }
    }

    trackVideoComplete(currentTime = 0, duration = 0) {
      if (!this.shouldTrackVideo() || this.hasCompleted) return;

      const safeDuration = this.normalizeNumber(duration) || this.normalizeNumber(currentTime);
      if (!safeDuration) return;

      this.hasCompleted = true;
      this.sendGA4Event('video_complete', {
        ...this.buildGA4VideoPayload(safeDuration, safeDuration),
        video_percent: 100
      });
    }

    startYouTubeProgressPolling() {
      this.stopYouTubeProgressPolling();

      this.youtubeProgressTimer = window.setInterval(() => {
        if (!this.player?.getCurrentTime || !this.player?.getDuration) return;

        this.trackVideoProgress(
          this.player.getCurrentTime(),
          this.player.getDuration()
        );
      }, 1000);
    }

    stopYouTubeProgressPolling() {
      if (!this.youtubeProgressTimer) return;

      window.clearInterval(this.youtubeProgressTimer);
      this.youtubeProgressTimer = null;
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      if (this.background) {
        const section = this.closest('.video-section');
        if (section) {
          section.addEventListener('click', this.play.bind(this));
        }
      }
    }

    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err); // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => {
          document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
        };
      }

      document.body.appendChild(s);
    }

    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', async () => {
          this.markPlayed();

          const [currentTime, duration] = await Promise.all([
            this.player.getCurrentTime(),
            this.player.getDuration()
          ]);

          this.trackVideoStart(currentTime, duration);
        });

        this.player.on('timeupdate', ({ seconds, duration }) => {
          this.trackVideoProgress(seconds, duration);
        });

        this.player.on('ended', ({ seconds, duration }) => {
          this.trackVideoProgress(seconds || duration, duration);
          this.trackVideoComplete(duration, duration);
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    initVideoTag() {
      this.player = this.querySelector('video');
      if (!this.player) return;

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.markPlayed();
        this.trackVideoStart(this.player.currentTime, this.player.duration);
      });

      this.player.addEventListener('timeupdate', () => {
        this.trackVideoProgress(this.player.currentTime, this.player.duration);
      });

      this.player.addEventListener('ended', () => {
        this.trackVideoProgress(this.player.duration, this.player.duration);
        this.trackVideoComplete(this.player.duration, this.player.duration);
      });

      this.addObserver(this.player);

      if (
        this.player.currentTime > 0 &&
        !this.player.paused &&
        !this.player.ended &&
        this.player.readyState > 2
      ) {
        this.markPlayed();
        this.trackVideoStart(this.player.currentTime, this.player.duration);
        this.trackVideoProgress(this.player.currentTime, this.player.duration);
      } else {
        const deferredMedia = this.closest('deferred-media');

        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        this.stopYouTubeProgressPolling();

        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        this.stopYouTubeProgressPolling();

        const duration = this.player.getDuration?.() || 0;
        this.trackVideoProgress(duration, duration);
        this.trackVideoComplete(duration, duration);

        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.markPlayed();

        const currentTime = this.player.getCurrentTime?.() || 0;
        const duration = this.player.getDuration?.() || 0;

        this.trackVideoStart(currentTime, duration);
        this.trackVideoProgress(currentTime, duration);
        this.startYouTubeProgressPolling();
      }
    }

    setupIframe(jsClass) {
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      if (!this.iframe) return;

      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
        return;
      }

      const playResult = this.player.play();
      if (playResult?.catch) {
        playResult.catch(() => {});
      }
    }

    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        this.stopYouTubeProgressPolling();

        if (this.player.getPlayerState() !== YT.PlayerState.PAUSED) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      if (this.observer) {
        this.observer.disconnect();
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      this.observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
